import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  aiPodBillingService,
  formatMoney,
  isLiveStatus,
  isUsableStatus,
  podOpenPath,
  podStatusLabel,
  usedUpLimits,
  type PodInvoice,
  type PodSnapshotFeature,
  type PodSubscription,
} from '../services/aiPodBillingService';
import { aiPodService, type AiPod } from '../services/aiPodService';
import { Button, Spinner } from '../components/ui/Button';
import Modal from '../components/ui/Modal';
import { Page, PageHeader, Section } from '../components/layout/primitives';
import { Tabs } from '../components/ui/Tabs';
import PodBreadcrumb from '../components/ai-pod/PodBreadcrumb';

type Tab = 'overview' | 'plan' | 'usage' | 'billing';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'plan', label: 'Plan features' },
  { id: 'usage', label: 'Usage & limits' },
  { id: 'billing', label: 'Billing history' },
];

const CATEGORY_LABELS: Record<string, string> = {
  access: 'Access',
  capacity: 'Capacity',
  workflow: 'Workflow',
  reporting: 'Reporting',
  support: 'Support',
  integration: 'Integrations',
};

const USAGE_LABELS: Record<string, { label: string; unit: string }> = {
  voice_minutes: { label: 'Voice call minutes', unit: 'minutes' },
  messages: { label: 'Messages', unit: 'messages' },
};

const invoiceStatusCls: Record<string, string> = {
  paid: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200',
  open: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200',
  uncollectible: 'bg-red-50 text-red-700 ring-1 ring-inset ring-red-200',
  void: 'bg-agentic-gray-100 text-agentic-text-muted ring-1 ring-inset ring-agentic-gray-200',
  draft: 'bg-agentic-gray-100 text-agentic-text-muted ring-1 ring-inset ring-agentic-gray-200',
};

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function featureValueText(f: PodSnapshotFeature): string {
  if (f.type === 'boolean') return f.value === true ? 'Included' : 'Not included';
  if (f.type === 'limit') {
    const n = Number(f.value);
    if (n === -1) return 'Unlimited';
    const unit = (f.unit ?? '').split('/').map((u) => u.trim());
    return `${n.toLocaleString()} ${n === 1 ? unit[0] ?? '' : unit[1] ?? unit[0] ?? ''}`.trim();
  }
  return String(f.value ?? '—');
}

function statusBadgeCls(sub: PodSubscription) {
  if (sub.status === 'canceled' || sub.status === 'unpaid' || sub.status === 'past_due') return 'bg-red-50 text-red-700 ring-red-200';
  if (sub.cancelAtPeriodEnd) return 'bg-amber-50 text-amber-700 ring-amber-200';
  if (sub.status === 'trialing') return 'bg-blue-50 text-blue-700 ring-blue-200';
  return 'bg-green-50 text-green-700 ring-green-200';
}

const PodManage: React.FC = () => {
  const { podId } = useParams<{ podId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const justCheckedOut = searchParams.get('checkout') === 'success';

  const [pod, setPod] = useState<AiPod | null>(null);
  const [sub, setSub] = useState<PodSubscription | null>(null);
  const [invoices, setInvoices] = useState<PodInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const pollRef = useRef(0);

  const load = useCallback(async () => {
    if (!podId) return;
    try {
      const [podRes, subRes] = await Promise.all([
        aiPodService.getAiPodById(podId),
        aiPodBillingService.getSubscription(podId),
      ]);
      setPod(podRes);
      setSub(subRes);
      setError(null);
      if (subRes?.source === 'stripe') {
        aiPodBillingService.listInvoices(podId).then((r) => setInvoices(r.invoices)).catch(() => setInvoices([]));
      }
    } catch (e: any) {
      setError(e?.message ?? 'Failed to load subscription');
    } finally {
      setLoading(false);
    }
  }, [podId]);

  useEffect(() => {
    void load();
  }, [load]);

  // After Stripe checkout the webhook may land a moment later; keep checking until the
  // subscription and its resources are ready.
  useEffect(() => {
    const waiting =
      (justCheckedOut && (!sub || !isLiveStatus(sub.status))) ||
      (!!sub && isUsableStatus(sub.status) && (sub.activationStatus === 'pending' || sub.activationStatus === 'running'));
    if (!waiting || pollRef.current >= 30) return;
    const t = setTimeout(() => {
      pollRef.current += 1;
      void load();
    }, 2500);
    return () => clearTimeout(t);
  }, [justCheckedOut, sub, load]);

  const run = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await fn();
      toast.success(success);
      await load();
    } catch (e: any) {
      toast.error(e?.message ?? 'Something went wrong');
    } finally {
      setBusy(null);
    }
  };

  const openBillingPortal = async () => {
    setBusy('portal');
    try {
      const { portalUrl } = await aiPodBillingService.openBillingPortal(window.location.href.split('?')[0]);
      window.location.href = portalUrl;
    } catch (e: any) {
      toast.error(e?.message ?? 'Could not open billing');
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Page>
        <div className="flex min-h-[60vh] items-center justify-center">
          <Spinner as="div" className="h-6 w-6 animate-spin rounded-full border-2 border-kv-agentic-blue border-t-transparent" />
        </div>
      </Page>
    );
  }

  const crumbs = [{ label: pod?.name ?? 'Pod', to: pod ? `/pods/${pod.id}` : undefined }, { label: 'Manage subscription' }];

  if (error || !sub) {
    return (
      <Page>
        <Section className="pt-8">
          <PodBreadcrumb items={crumbs} />
          <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4 text-center">
            {justCheckedOut && !error ? (
              <>
                <Spinner as="div" className="h-6 w-6 animate-spin rounded-full border-2 border-kv-agentic-blue border-t-transparent" />
                <p className="text-base font-semibold text-agentic-text-primary">Confirming your payment…</p>
                <p className="text-sm text-agentic-text-muted">This usually takes a few seconds.</p>
              </>
            ) : (
              <>
                <p className="text-base font-semibold text-agentic-text-primary">{error ?? 'You are not subscribed to this pod.'}</p>
                {podId && (
                  <Button variant="primary" onClick={() => navigate(`/pods/${podId}/plans`)}>
                    View plans
                  </Button>
                )}
              </>
            )}
          </div>
        </Section>
      </Page>
    );
  }

  const plan = sub.snapshot.plan;
  const live = isLiveStatus(sub.status);
  const usable = isUsableStatus(sub.status);
  const isTrial = sub.status === 'trialing';
  const openPath = podOpenPath(sub);
  const pending = sub.pendingSnapshot?.plan;

  const groupedFeatures: Record<string, PodSnapshotFeature[]> = {};
  sub.snapshot.features.forEach((f) => {
    (groupedFeatures[f.category] ??= []).push(f);
  });

  return (
    <Page>
      <Section className="pt-8 pb-0">
        <PodBreadcrumb items={crumbs} className="mb-0" />
      </Section>
      <PageHeader title="Manage subscription" className="py-4 md:py-6" />

      <Section className="pt-0 space-y-6">
        {justCheckedOut && live && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3">
            <p className="text-sm text-green-800">
              {isTrial ? `Your free trial has started — it runs until ${formatDate(sub.trialEnd)}.` : 'Payment confirmed. Your subscription is active.'}
            </p>
            <Button tone="custom" className="text-sm text-green-800 underline" onClick={() => setSearchParams({})}>
              Dismiss
            </Button>
          </div>
        )}

        {/* Summary */}
        <div className="rounded-panel bg-white p-5 shadow-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-kv-agentic-purple to-kv-agentic-blue text-lg font-bold text-white">
                {(pod?.name ?? sub.snapshot.pod.name).charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-agentic-text-muted">{sub.snapshot.pod.name}</p>
                <h2 className="text-xl font-bold leading-tight text-agentic-text-primary">{plan.displayName}</h2>
                <p className="mt-0.5 text-sm text-agentic-text-secondary">
                  {plan.amountMinor === 0 ? 'Free' : `${formatMoney(plan.amountMinor, plan.currency)} / ${plan.interval}`}
                  {sub.source === 'granted' && <span className="text-agentic-text-muted"> · provided by your administrator</span>}
                </p>
              </div>
            </div>
            <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ring-1 ring-inset ${statusBadgeCls(sub)}`}>
              {podStatusLabel(sub)}
            </span>
          </div>

          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {isTrial && (
              <div className="rounded-xl bg-agentic-gray-50 px-4 py-3">
                <p className="text-xs text-agentic-text-muted">Free trial ends</p>
                <p className="text-sm font-semibold text-agentic-text-primary">{formatDate(sub.trialEnd)}</p>
              </div>
            )}
            {!isTrial && (
              <div className="rounded-xl bg-agentic-gray-50 px-4 py-3">
                <p className="text-xs text-agentic-text-muted">Current period started</p>
                <p className="text-sm font-semibold text-agentic-text-primary">{formatDate(sub.currentPeriodStart)}</p>
              </div>
            )}
            <div className="rounded-xl bg-agentic-gray-50 px-4 py-3">
              <p className="text-xs text-agentic-text-muted">
                {sub.status === 'canceled' ? 'Ended on' : sub.cancelAtPeriodEnd ? 'Ends on' : isTrial ? 'First charge on' : 'Renews on'}
              </p>
              <p className="text-sm font-semibold text-agentic-text-primary">
                {formatDate(sub.status === 'canceled' ? sub.endedAt : sub.currentPeriodEnd)}
              </p>
            </div>
            <div className="rounded-xl bg-agentic-gray-50 px-4 py-3">
              <p className="text-xs text-agentic-text-muted">Billing</p>
              <p className="text-sm font-semibold capitalize text-agentic-text-primary">
                {sub.source === 'stripe' ? `${plan.interval}ly` : sub.source === 'granted' ? 'Provided' : 'Free'}
              </p>
            </div>
          </div>

          {/* State banners */}
          {(sub.status === 'past_due' || sub.status === 'unpaid') && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3">
              <p className="text-sm text-red-800">
                Your last payment failed, so this pod is paused. Update your payment method and pay the open invoice to turn it back on.
              </p>
              <Button variant="primary" size="sm" loading={busy === 'portal'} onClick={openBillingPortal}>
                Fix payment
              </Button>
            </div>
          )}
          {usable && usedUpLimits(sub).length > 0 && (
            <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-800">
                Your plan is fully used for this period:{' '}
                {usedUpLimits(sub)
                  .map((u) => `${USAGE_LABELS[u.hook]?.label.toLowerCase() ?? u.hook} (${u.used.toLocaleString()} of ${u.limit.toLocaleString()})`)
                  .join(' and ')}
                .{sub.usagePeriod?.end ? ` The allowance resets on ${formatDate(sub.usagePeriod.end)}.` : ''} Your subscription stays active.
              </p>
            </div>
          )}
          {sub.status === 'canceled' && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-agentic-gray-200 bg-agentic-gray-50 px-4 py-3">
              <p className="text-sm text-agentic-text-secondary">
                This subscription has ended. Your workflow and chatbot are paused and their data is kept — subscribe again to turn them back on.
              </p>
              <Button variant="primary" size="sm" onClick={() => navigate(`/pods/${podId}/plans`)}>
                Subscribe again
              </Button>
            </div>
          )}
          {usable && sub.activationStatus !== 'done' && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
              <p className="text-sm text-amber-800">
                {sub.activationStatus === 'failed'
                  ? `Setting up your pod failed${sub.activationError ? `: ${sub.activationError}` : ''}.`
                  : 'Setting up your pod — your workflow and agents will be ready shortly.'}
              </p>
              {sub.activationStatus === 'failed' && (
                <Button
                  variant="primary"
                  size="sm"
                  loading={busy === 'activate'}
                  onClick={() => run('activate', () => aiPodBillingService.retryActivation(podId!), 'Pod set up')}
                >
                  Try again
                </Button>
              )}
            </div>
          )}
          {pending && sub.pendingEffectiveAt && (
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
              <p className="text-sm text-blue-800">
                Changing to <strong>{pending.displayName}</strong>
                {pending.amountMinor === 0 ? ' (free)' : ` (${formatMoney(pending.amountMinor, pending.currency)} / ${pending.interval})`} on{' '}
                {formatDate(sub.pendingEffectiveAt)}.
              </p>
              <Button
                variant="ghost"
                size="sm"
                loading={busy === 'undo'}
                onClick={() => run('undo', () => aiPodBillingService.cancelPendingChange(podId!), 'Scheduled change cancelled')}
              >
                Keep current plan
              </Button>
            </div>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            {usable && openPath && (
              <Button variant="primary" size="md" onClick={() => navigate(openPath)}>
                Open
              </Button>
            )}
            {live && (
              <Button variant="ghost" size="md" onClick={() => navigate(`/pods/${podId}/plans`)}>
                Change plan
              </Button>
            )}
            {sub.source === 'stripe' && live && (
              <Button variant="ghost" size="md" loading={busy === 'portal'} onClick={openBillingPortal}>
                Payment methods
              </Button>
            )}
            {live && sub.source !== 'granted' &&
              (sub.cancelAtPeriodEnd ? (
                <Button
                  variant="ghost"
                  size="md"
                  loading={busy === 'reactivate'}
                  onClick={() => run('reactivate', () => aiPodBillingService.reactivateSubscription(podId!), 'Subscription will renew')}
                  className="border-green-300 text-green-700 hover:bg-green-50"
                >
                  Keep subscription
                </Button>
              ) : (
                <Button
                  tone="custom"
                  onClick={() => setConfirmCancel(true)}
                  className="rounded-xl border border-agentic-gray-300 px-4 py-2 text-sm font-semibold text-agentic-text-secondary transition-colors hover:border-red-300 hover:text-red-600"
                >
                  Cancel subscription
                </Button>
              ))}
          </div>
        </div>

        <Tabs variant="underline-bold" items={TABS} value={activeTab} onChange={(id) => setActiveTab(id as Tab)} />

        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2 rounded-panel bg-white p-5 text-sm shadow-card">
              <h3 className="mb-2 text-sm font-bold text-agentic-text-primary">What's set up for you</h3>
              {/* Only what this pod provides: a chat pod has no workflow, a workflow pod no chatbot. */}
              {sub.snapshot.pod.channel !== 'chat' && (
                <div className="flex justify-between">
                  <span className="text-agentic-text-muted">Workflow</span>
                  <span className="font-semibold text-agentic-text-primary">{sub.workflowId ? (sub.resourcesState === 'paused' ? 'Paused' : 'Ready') : '—'}</span>
                </div>
              )}
              {sub.snapshot.pod.channel !== 'workflow' && (
                <div className="flex justify-between">
                  <span className="text-agentic-text-muted">Chatbot</span>
                  <span className="font-semibold text-agentic-text-primary">{sub.chatbotId ? (sub.resourcesState === 'paused' ? 'Paused' : 'Ready') : '—'}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-agentic-text-muted">Subscribed since</span>
                <span className="font-semibold text-agentic-text-primary">{formatDate(sub.createdAt)}</span>
              </div>
            </div>
            <div className="space-y-2 rounded-panel bg-white p-5 text-sm shadow-card">
              <h3 className="mb-2 text-sm font-bold text-agentic-text-primary">Usage this period</h3>
              {(sub.usage ?? []).length === 0 ? (
                <p className="text-agentic-text-muted">Your plan has no usage limits.</p>
              ) : (
                (sub.usage ?? []).map((u) => (
                  <div key={u.hook} className="flex justify-between">
                    <span className="text-agentic-text-muted">{USAGE_LABELS[u.hook]?.label ?? u.hook}</span>
                    <span className="font-semibold text-agentic-text-primary">
                      {u.used.toLocaleString()} / {u.limit === -1 ? 'Unlimited' : u.limit.toLocaleString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {activeTab === 'plan' && (
          <div className="overflow-hidden rounded-panel bg-white shadow-card">
            <p className="border-b border-agentic-gray-100 px-6 py-3 text-xs text-agentic-text-muted">
              What you bought on {formatDate(sub.snapshot.takenAt)}. Later changes to the plan by the provider don't affect your subscription.
            </p>
            <div className="divide-y divide-agentic-gray-100">
              {Object.entries(groupedFeatures).map(([cat, items]) => (
                <div key={cat} className="px-6 py-5">
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-agentic-text-muted">{CATEGORY_LABELS[cat] ?? cat}</p>
                  <div className="space-y-2">
                    {items.map((f) => (
                      <div key={f.featureId} className="flex items-center justify-between gap-4">
                        <span className="text-sm text-agentic-text-secondary">{f.name}</span>
                        <span
                          className={`text-sm font-semibold ${
                            f.type === 'boolean' && f.value !== true ? 'text-agentic-text-muted' : 'text-agentic-text-primary'
                          }`}
                        >
                          {featureValueText(f)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="overflow-hidden rounded-panel bg-white shadow-card">
            <p className="border-b border-agentic-gray-100 px-6 py-3 text-xs text-agentic-text-muted">
              Current period: {formatDate(sub.usagePeriod?.start)} – {formatDate(sub.usagePeriod?.end)}. Allowances reset each billing period.
            </p>
            {(sub.usage ?? []).length === 0 ? (
              <p className="px-6 py-10 text-center text-sm text-agentic-text-muted">Your plan has no usage limits.</p>
            ) : (
              <div className="divide-y divide-agentic-gray-100">
                {(sub.usage ?? []).map((u) => {
                  const pct = u.limit > 0 ? Math.min(100, Math.round((u.used / u.limit) * 100)) : 0;
                  const meta = USAGE_LABELS[u.hook] ?? { label: u.hook, unit: '' };
                  return (
                    <div key={u.hook} className="px-6 py-5">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-agentic-text-primary">{meta.label}</span>
                        <span className="text-sm text-agentic-text-secondary tabular-nums">
                          {u.used.toLocaleString()} of {u.limit === -1 ? 'unlimited' : u.limit.toLocaleString()} {meta.unit}
                        </span>
                      </div>
                      {u.limit > 0 && (
                        <div className="mt-2 h-2 overflow-hidden rounded-full bg-agentic-gray-100">
                          <div
                            className={`h-full rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-kv-agentic-blue'}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      )}
                      {u.limit > 0 && pct >= 100 && (
                        <p className="mt-2 text-xs text-red-600">Limit reached — new requests are refused until the next period or an upgrade.</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="overflow-hidden rounded-panel bg-white shadow-card">
            {invoices.length === 0 ? (
              <p className="px-6 py-12 text-center text-sm text-agentic-text-muted">
                {sub.source === 'stripe' ? 'No invoices yet.' : 'This subscription is not billed.'}
              </p>
            ) : (
              <>
                <div className="grid grid-cols-[1fr_140px_100px_64px] items-center gap-4 border-b border-agentic-gray-100 px-6 py-2.5 text-xs font-semibold uppercase tracking-wide text-agentic-text-muted">
                  <span>Date</span>
                  <span className="text-right">Amount</span>
                  <span>Status</span>
                  <span />
                </div>
                <div className="max-h-[400px] divide-y divide-agentic-gray-100 overflow-y-auto">
                  {invoices.map((inv) => (
                    <div key={inv.id} className="grid grid-cols-[1fr_140px_100px_64px] items-center gap-4 px-6 py-4 transition-colors hover:bg-agentic-gray-50">
                      <div>
                        <p className="text-sm font-semibold text-agentic-text-primary">{formatDate(inv.created)}</p>
                        {inv.description && <p className="mt-0.5 max-w-[260px] truncate text-xs text-agentic-text-muted">{inv.description}</p>}
                      </div>
                      <p className="text-right text-sm font-bold tabular-nums text-agentic-text-primary">
                        {formatMoney(inv.status === 'paid' ? inv.amountPaid : inv.amountDue, inv.currency)}
                      </p>
                      <span className={`w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${invoiceStatusCls[inv.status ?? ''] ?? invoiceStatusCls.draft}`}>
                        {inv.status ?? '—'}
                      </span>
                      <div className="flex items-center justify-end gap-1.5">
                        {(inv.hostedInvoiceUrl || inv.invoicePdf) && (
                          <a
                            href={inv.hostedInvoiceUrl ?? inv.invoicePdf ?? '#'}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="View invoice"
                            className="rounded-lg p-1.5 text-agentic-text-muted transition-colors hover:bg-agentic-gray-100 hover:text-kv-agentic-blue"
                          >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        <Modal
          isOpen={confirmCancel}
          onClose={() => setConfirmCancel(false)}
          title="Cancel subscription?"
          size="sm"
          footer={
            <>
              <Button variant="ghost" size="md" onClick={() => setConfirmCancel(false)}>
                Keep subscription
              </Button>
              <Button
                variant="primary"
                size="md"
                loading={busy === 'cancel'}
                className="bg-red-600 hover:bg-red-700 focus-visible:ring-red-500"
                onClick={async () => {
                  await run('cancel', () => aiPodBillingService.cancelSubscription(podId!), 'Subscription cancelled');
                  setConfirmCancel(false);
                }}
              >
                Yes, cancel
              </Button>
            </>
          }
        >
          <div className="space-y-3 text-sm leading-relaxed text-agentic-text-secondary">
            {isTrial ? (
              <p>
                Your free trial continues until <strong className="text-agentic-text-primary">{formatDate(sub.currentPeriodEnd)}</strong> and you
                won't be charged.
              </p>
            ) : (
              <p>
                You keep full access until <strong className="text-agentic-text-primary">{formatDate(sub.currentPeriodEnd)}</strong> and won't be
                charged again.
              </p>
            )}
            <p>After that, the pod's workflow and chatbot are paused and its agents switched off. Your configuration and data are kept, and subscribing again turns everything back on.</p>
            {pending && <p>Your scheduled change to {pending.displayName} will be dropped.</p>}
          </div>
        </Modal>
      </Section>
    </Page>
  );
};

export default PodManage;
